/**
 * useRichTextEditor
 *
 * Composable that encapsulates the core logic for the RichTextBoxV2 component.
 * Responsibilities:
 *  - Badge (variable pill) creation and removal
 *  - Plain-text extraction with correct character counting
 *    (each variable badge counts as 1 character)
 *  - HTML sanitisation via DOMPurify
 *  - Cursor / selection save & restore helpers
 *  - HTML insertion at the current cursor position
 *  - Conversion of a raw string (with <variable> tokens) into badge HTML
 *  - JSON pretty-print renderer with badge support
 */

import { ref } from 'vue';
import DOMPurify from 'dompurify';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * HTML tag names that must NOT be treated as variable placeholders when
 * we scan for <word> patterns inside raw string values.
 */
const RESERVED_HTML_TAGS = new Set([
  'br', 'div', 'p', 'span', 'b', 'i', 'u', 'strong', 'em',
  'ul', 'ol', 'li', 'a', 'img', 'input', 'button', 'hr',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'table', 'thead', 'tbody', 'tr', 'td', 'th',
  'pre', 'code', 'blockquote', 'form', 'label', 'select',
  'option', 'textarea', 'svg', 'path', 'circle', 'rect',
  'g', 'style', 'script', 'link', 'meta', 'head', 'body', 'html',
  'iframe', 'video', 'audio', 'source', 'canvas',
  'section', 'article', 'nav', 'footer', 'header', 'main', 'aside',
  'details', 'summary', 'figure', 'figcaption', 'mark', 'small',
  'cite', 'abbr', 'data', 'time', 'var', 'samp', 'kbd',
  'sub', 'sup', 'del', 'ins', 'map', 'area', 'object', 'param',
  'embed', 'track', 'wbr', 'col', 'colgroup', 'caption',
  'fieldset', 'legend', 'datalist', 'output', 'progress',
  'meter', 'template', 'noscript',
]);

// ---------------------------------------------------------------------------
// Utility helpers (module-level, no reactivity needed)
// ---------------------------------------------------------------------------

/**
 * Generates a unique ID string suitable for DOM element IDs.
 * Format: rtb-<random>-<timestamp>
 */
function generateUniqueId() {
  return `rtb-${Math.random().toString(36).slice(2, 9)}-${Date.now()}`;
}

/**
 * Builds the badge HTML string for a given variable token.
 *
 * @param {string} variable  – raw variable string, e.g. "<userfirstname>"
 * @returns {string}         – HTML string for the badge span
 */
function buildBadgeHtml(variable) {
  const uniqueId = generateUniqueId();
  // Strip surrounding angle brackets for the display label
  const displayLabel = variable.replace(/[<>]/g, '');
  return (
    `<span ` +
    `class="rtb-badge" ` +
    `contenteditable="false" ` +
    `id="rtb-badge-${uniqueId}" ` +
    `data-value="${variable}" ` +
    `data-variable-badge="true"` +
    `>` +
    ` ${displayLabel} ` +
    `<span class="rtb-badge-remove" data-badge-id="rtb-badge-${uniqueId}" data-remove-variable="${variable}">×</span>` +
    `</span>`
  );
}

/**
 * Converts raw variable tokens ( <variableName> ) inside a plain string into
 * badge HTML. HTML entities (&lt; &gt;) are also matched.
 *
 * @param {string} rawHtml        – the input string / HTML
 * @param {string} [contentType]  – 'TEXT' | 'JSON'
 * @returns {string}              – HTML with variable tokens replaced by badge spans
 */
function convertTokensToBadges(rawHtml, contentType = 'TEXT') {
  let processed = rawHtml ?? '';

  // In TEXT mode convert newline characters to <br> so they are visible
  if (contentType === 'TEXT' && typeof processed === 'string') {
    processed = processed.replace(/\n/g, '<br>');
  }

  // Match both HTML-encoded &lt;word&gt; and literal <word>
  return processed.replace(
    /&lt;([a-zA-Z0-9_]+)&gt;|<([a-zA-Z0-9_]+)>/g,
    (match, encodedVar, literalVar) => {
      const variable = encodedVar ?? literalVar;
      if (RESERVED_HTML_TAGS.has(variable.toLowerCase())) {
        return match; // keep real HTML tags untouched
      }
      return buildBadgeHtml(`<${variable}>`);
    }
  );
}

/**
 * Sanitises HTML via DOMPurify while preserving:
 *  - variable placeholder tokens  (<word>)
 *  - <br> tags
 *  - badge spans (identified by data-variable-badge)
 *
 * Strategy: temporarily escape the tokens, sanitise, then restore.
 *
 * @param {string} html
 * @returns {string}
 */
function sanitiseHtml(html) {
  // 1. Protect <word> variable tokens that are NOT real HTML tags
  let protected_ = html.replace(/<([a-zA-Z0-9_]+)>/g, (match, tag) => {
    if (RESERVED_HTML_TAGS.has(tag.toLowerCase())) return match;
    return `%%VAR_${tag}%%`;
  });

  // 2. Protect bare <br> tags
  protected_ = protected_.replace(/<br\s*\/?>/gi, '%%BR%%');

  // 3. Run DOMPurify (allow data-* attributes and contenteditable for badges)
  protected_ = DOMPurify.sanitize(protected_, {
    ADD_ATTR: ['contenteditable', 'data-value', 'data-variable-badge', 'data-badge-id', 'data-remove-variable'],
  });

  // 4. Restore protected tokens
  protected_ = protected_.replace(/%%VAR_([a-zA-Z0-9_]+)%%/g, '<$1>');
  protected_ = protected_.replace(/%%BR%%/g, '<br>');

  return protected_;
}

/**
 * Renders a JSON string as formatted HTML, converting variable tokens inside
 * string values to badges.  Falls back to a plain pre-wrap div for invalid JSON.
 *
 * @param {string} jsonString
 * @returns {string} – HTML string
 */
function renderJsonWithBadges(jsonString) {
  let parsed;
  try {
    parsed = JSON.parse(jsonString);
  } catch (_err) {
    return `<div style="white-space:pre-wrap;font-family:monospace;">${jsonString}</div>`;
  }

  function renderValue(val) {
    if (typeof val === 'string') {
      return `"${convertTokensToBadges(val, 'TEXT')}"`;
    }
    if (typeof val === 'number' || typeof val === 'boolean') {
      return String(val);
    }
    if (val === null) return 'null';
    if (Array.isArray(val)) {
      return `[${val.map(renderValue).join(', ')}]`;
    }
    if (typeof val === 'object') {
      // Object keys can themselves hold variable tokens (e.g. a custom field
      // used as the parameter name in a third-party API request body), so they
      // go through the same token->badge conversion as string values. Without
      // this the raw `<token>` text reaches innerHTML, where the HTML parser
      // treats it as an unknown element and the key renders as an empty string.
      const entries = Object.entries(val)
        .map(([k, v]) => `<span style="color:#922">"${convertTokensToBadges(k, 'TEXT')}"</span>: ${renderValue(v)}`)
        .join(',<br>');
      return `{<br>${entries}<br>}`;
    }
    return '';
  }

  return `<div style="white-space:pre-wrap;font-family:monospace;">${renderValue(parsed)}</div>`;
}

// ---------------------------------------------------------------------------
// Composable
// ---------------------------------------------------------------------------

/**
 * @param {import('vue').Ref<HTMLElement|null>} editorRef – ref to the contenteditable div
 * @param {object} props – component props (reactive)
 */
export function useRichTextEditor(editorRef, props) {
  // Saved selection range for cursor restore after popover/modal interactions
  const savedRange = ref(null);

  // ------------------------------------------------------------------
  // Selection helpers
  // ------------------------------------------------------------------

  /** Saves the current window selection range if it is inside the editor. */
  function saveSelection() {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && editorRef.value?.contains(sel.anchorNode)) {
      savedRange.value = sel.getRangeAt(0).cloneRange();
    }
  }

  /** Restores the previously saved selection range. */
  function restoreSelection() {
    if (!savedRange.value) return;
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(savedRange.value);
    }
  }

  // ------------------------------------------------------------------
  // HTML insertion
  // ------------------------------------------------------------------

  /**
   * Inserts arbitrary HTML at the current cursor position (or saved range).
   * After insertion the cursor is placed immediately after the inserted content.
   *
   * @param {string} html – HTML string to insert
   */
  function insertHtmlAtCursor(html) {
    if (!editorRef.value) return;
    editorRef.value.focus();

    const sel = window.getSelection();
    let range = null;

    // Prefer the saved range if it is still within the editor
    if (savedRange.value && editorRef.value.contains(savedRange.value.startContainer)) {
      range = savedRange.value;
      sel?.removeAllRanges();
      sel?.addRange(range);
    } else if (sel && sel.rangeCount > 0 && editorRef.value.contains(sel.anchorNode)) {
      range = sel.getRangeAt(0);
    } else {
      // Fallback: place cursor at end of editor
      range = document.createRange();
      range.selectNodeContents(editorRef.value);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }

    // Build a document fragment from the HTML string
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const fragment = document.createDocumentFragment();
    let lastNode = null;
    while (tempDiv.firstChild) {
      lastNode = fragment.appendChild(tempDiv.firstChild);
    }

    range.deleteContents();
    range.insertNode(fragment);

    // Move cursor after inserted content
    if (lastNode) {
      range.setStartAfter(lastNode);
      range.collapse(true);
      sel?.removeAllRanges();
      sel?.addRange(range);
      savedRange.value = range.cloneRange();
    }
  }

  // ------------------------------------------------------------------
  // Badge insertion
  // ------------------------------------------------------------------

  /**
   * Inserts a variable badge at the cursor position.
   *
   * @param {string} variable – e.g. "<userfirstname>" or "userfirstname"
   */
  function insertVariableBadge(variable) {
    if (props.readonly || props.disabled) return;
    // Normalise: ensure angle brackets
    const normalised = variable.startsWith('<') ? variable : `<${variable}>`;
    const badgeHtml = buildBadgeHtml(normalised);
    insertHtmlAtCursor(badgeHtml);
  }

  // ------------------------------------------------------------------
  // Badge removal
  // ------------------------------------------------------------------

  /**
   * Removes a badge element from the editor by its element ID.
   *
   * @param {string} badgeId  – the id attribute of the badge span (rtb-badge-xxx)
   * @returns {string|null}   – the data-value of the removed badge, or null
   */
  function removeBadgeById(badgeId) {
    if (!editorRef.value || props.readonly || props.disabled) return null;
    const badge = editorRef.value.querySelector(`#${badgeId}`);
    if (!badge) return null;
    const value = badge.getAttribute('data-value');
    badge.remove();
    return value;
  }

  // ------------------------------------------------------------------
  // Text extraction & character counting
  // ------------------------------------------------------------------

  /**
   * Extracts the "logical" plain text from the editor HTML.
   *  - Each variable badge is replaced with its <token> value (counts as one
   *    logical unit when measuring display, but the full token for API output)
   *  - <br> tags become newline characters in TEXT mode
   *  - Non-breaking spaces are normalised to regular spaces
   *
   * @param {string} html        – editor innerHTML
   * @param {string} contentType – 'TEXT' | 'JSON'
   * @returns {string}
   */
  function extractPlainText(html, contentType = 'TEXT') {
    if (!html) return '';

    const clone = document.createElement('div');
    clone.innerHTML = html;

    // Replace badge spans with their token value
    clone.querySelectorAll('[data-variable-badge="true"]').forEach((badge) => {
      const tokenValue = badge.getAttribute('data-value') ?? '';
      badge.replaceWith(document.createTextNode(tokenValue));
    });

    if (contentType === 'TEXT') {
      // Replace <br> with newline
      clone.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
      // Paragraph / div blocks add implicit newlines
      clone.querySelectorAll('div, p').forEach((block) => {
        if (block.nextSibling) block.after('\n');
      });
    }

    const temp = document.createElement('div');
    temp.innerHTML = clone.innerHTML;
    let text = temp.textContent ?? temp.innerText ?? '';

    // Normalise
    text = text.replace(/\u00A0/g, ' ').trim();
    if (contentType === 'TEXT') {
      text = text.replace(/\\n/g, '\n');
      text = text.split('\n').map((line) => line.replace(/\s+$/, '')).join('\n');
      text = text.replace(/^\n+|\n+$/g, '');
    }

    return text;
  }

  /**
   * Computes the character count where:
   *  - Each regular character counts as 1
   *  - Each variable badge counts as 1
   *  - Each <br> (newline) counts as 1
   *
   * @param {string} html – editor innerHTML
   * @returns {number}
   */
  function computeCharacterCount(html) {
    if (!html) return 0;

    const clone = document.createElement('div');
    clone.innerHTML = html;

    // Replace each variable badge with a single placeholder char (counts as 1).
    // \x01 (SOH control char) never appears in normal user text.
    clone.querySelectorAll('[data-variable-badge="true"]').forEach((badge) => {
      badge.replaceWith(document.createTextNode('\x01'));
    });

    // Replace each <br> element with a newline character (counts as 1).
    clone.querySelectorAll('br').forEach((br) => {
      br.replaceWith(document.createTextNode('\n'));
    });

    // Read textContent directly from the modified clone — no re-serialization
    // through innerHTML, which avoids browser-specific mangling of control chars.
    const textContent = (clone.textContent ?? '').replace(/\u00A0/g, ' ');

    return textContent.length;
  }

  // ------------------------------------------------------------------
  // Initialise editor content
  // ------------------------------------------------------------------

  /**
   * Converts a raw prop value into editor-ready HTML and sets the editor
   * innerHTML.  Should be called once on mount and when external value changes.
   *
   * @param {string} rawValue   – the prop value string
   * @param {string} contentType
   */
  function initialiseEditorContent(rawValue, contentType = 'TEXT') {
    if (!editorRef.value) return;
    let html = '';
    if (contentType === 'JSON') {
      html = renderJsonWithBadges(rawValue ?? '');
    } else {
      html = convertTokensToBadges(rawValue ?? '', contentType);
    }
    // Sanitise before writing to the DOM to guard against XSS in prop values.
    const sanitised = sanitiseHtml(html);
    editorRef.value.innerHTML = sanitised;
    return sanitised;
  }

  // ------------------------------------------------------------------
  // Validation
  // ------------------------------------------------------------------

  /**
   * Validates the decoded plain text for angle-bracket integrity.
   * Only valid <word> variable tokens are allowed; stray or malformed
   * angle characters produce an error.
   *
   * @param {string} decodedText
   * @param {string} contentType
   * @param {boolean} required
   * @returns {{ valid: boolean, error: string }}
   */
  function validateContent(decodedText, contentType = 'TEXT', required = false) {
    if (required && (!decodedText || decodedText.trim() === '')) {
      return { valid: false, error: 'This field is required.' };
    }

    if (contentType === 'JSON') {
      if (!decodedText) return { valid: true, error: '' };
      let parsed;
      try {
        parsed = JSON.parse(decodedText);
      } catch (_err) {
        return { valid: false, error: 'Invalid JSON format.' };
      }

      function checkAngles(val, path = '') {
        if (typeof val === 'string') {
          const angleRegex = /<[^>]*>|[<>]/g;
          let match;
          while ((match = angleRegex.exec(val)) !== null) {
            const str = match[0];
            if (str.startsWith('<') && str.endsWith('>')) {
              if (!/^<\w+>$/.test(str)) {
                return { valid: false, error: `Invalid variable placeholder at ${path || 'root'}: ${str}` };
              }
            } else {
              return { valid: false, error: `Invalid angle character in JSON at ${path || 'root'}.` };
            }
          }
        } else if (Array.isArray(val)) {
          for (let i = 0; i < val.length; i++) {
            const result = checkAngles(val[i], `${path}[${i}]`);
            if (!result.valid) return result;
          }
        } else if (val && typeof val === 'object') {
          for (const k of Object.keys(val)) {
            const result = checkAngles(val[k], path ? `${path}.${k}` : k);
            if (!result.valid) return result;
          }
        }
        return { valid: true, error: '' };
      }

      return checkAngles(parsed);
    }

    // TEXT mode
    const angleRegex = /<[^>]*>|[<>]/g;
    let match;
    while ((match = angleRegex.exec(decodedText)) !== null) {
      const str = match[0];
      if (str.startsWith('<') && str.endsWith('>')) {
        if (!/^<\w+>$/.test(str)) {
          return { valid: false, error: 'Message contains an invalid < or > character.' };
        }
      } else {
        return { valid: false, error: 'Message contains an invalid < or > character.' };
      }
    }

    return { valid: true, error: '' };
  }

  // ------------------------------------------------------------------
  // Public API
  // ------------------------------------------------------------------

  return {
    savedRange,
    saveSelection,
    restoreSelection,
    insertHtmlAtCursor,
    insertVariableBadge,
    removeBadgeById,
    extractPlainText,
    computeCharacterCount,
    initialiseEditorContent,
    validateContent,
    // Expose low-level helpers for tests / advanced use
    convertTokensToBadges,
    sanitiseHtml,
    renderJsonWithBadges,
    buildBadgeHtml,
  };
}
